/*
Context for Matomo content tracking (see src/utils/matomo.ts).

Two levels, because the tracked values come from two levels:

1. The content name describes the *place* — page title plus module. Only the page that
   renders the module knows both, the card does not. `ContentTrackingModule` sets it
   there once, instead of threading it through sliders and grids as props.
2. Content piece and target describe the *content* — only the card knows those.
   `ContentBlockProvider` exposes the complete block to descendants, so that e.g. the
   bookmark button inside the card can fire its interaction without receiving the values
   as props.

Without the module context the hooks return `undefined` and nothing is tracked. So a page
only gets content tracking once it wraps its modules — pages without
`ContentTrackingModule` stay untouched.
*/

'use client';

import { createContext, useContext, useMemo, ReactNode } from 'react';
import { ModuleType } from '@/shared/types/api/moduleType';
import { MatomoContentBlock, buildContentName } from '@/utils/matomo';

const ContentNameContext = createContext<string | undefined>(undefined);
const ContentBlockContext = createContext<MatomoContentBlock | undefined>(undefined);

/**
 * Names the place the contained cards sit in. Renders no DOM itself.
 *
 * @param pageTitle page title as in the Matomo report, see `MATOMO_PAGE_TITLES`
 * @param moduleType module type from the page's API response
 */
export function ContentTrackingModule({
  pageTitle,
  moduleType,
  children,
}: {
  pageTitle: string;
  moduleType: ModuleType;
  children: ReactNode;
}) {
  const contentName = useMemo(
    () => buildContentName(pageTitle, moduleType),
    [pageTitle, moduleType],
  );

  return (
    <ContentNameContext.Provider value={contentName}>
      {children}
    </ContentNameContext.Provider>
  );
}

/** Content name of the surrounding module, `undefined` outside {@link ContentTrackingModule}. */
export function useContentName(): string | undefined {
  return useContext(ContentNameContext);
}

/** Exposes a card's block to its descendants (e.g. `ActionFavorite`). */
export function ContentBlockProvider({
  block,
  children,
}: {
  block: MatomoContentBlock | undefined;
  children: ReactNode;
}) {
  return (
    <ContentBlockContext.Provider value={block}>{children}</ContentBlockContext.Provider>
  );
}

/**
 * Block of the surrounding card, `undefined` when the card is not tracked — in that
 * case the caller must not send an interaction.
 */
export function useContentBlock(): MatomoContentBlock | undefined {
  return useContext(ContentBlockContext);
}
